<template>
  <transition name="esc-pop">
    <div
      v-if="visible"
      class="escalation-alert"
      :class="{ 'esc-flash': flashing }"
      @click="goToList"
      title="Escalades en attente — cliquez pour traiter"
    >
      <div class="esc-icon"><i class="fad fa-triangle-exclamation"></i></div>
      <div class="esc-body">
        <span class="esc-count">{{ count }}</span>
        <span class="esc-label">escalade{{ count > 1 ? 's' : '' }} en attente d'un agent</span>
      </div>
      <div class="esc-cta">Cliquez pour traiter <i class="fad fa-arrow-right"></i></div>
    </div>
  </transition>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { api } from "@/services/Api";
import { auth } from "@/services/Auth";
import { centrifugoManager } from "@/services/CentrifugoManager";

/**
 * Signal global d'escalade : bannière flottante très voyante (rouge, pulsante) dès qu'une ou
 * plusieurs conversations sont escaladées en attente agent. Réattire l'attention périodiquement
 * (pulse visuel ; pulse + son sur NOUVELLE escalade). Clic -> liste des escalades.
 * Temps réel via Centrifugo (canal chat:agents) + filet de polling. Visible managers uniquement.
 */
@Component
export default class EscalationAlert extends Vue {
  count = 0;
  flashing = false;

  private pollTimer: any = null;
  private attentionTimer: any = null;
  private debounce: any = null;
  private flashTimeout: any = null;
  private realtimeReady = false;
  // Handler temps réel stable : on ne doit retirer QUE le nôtre au destroy
  // (le canal chat:agents est partagé avec la console).
  private rtHandler = () => {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.refresh(), 700);
  };

  get visible(): boolean {
    // Pas sur la liste des escalades elle-même : l'utilisateur y est déjà.
    return this.count > 0 && this.canSee && this.$route.name !== "chat-escalations";
  }

  get canSee(): boolean {
    try {
      return (
        auth.hasRole("ROLE_MANAGER") ||
        auth.hasRole("ROLE_SUPER_ADMIN") ||
        auth.hasRole("ROLE_AGENT")
      );
    } catch (e) {
      return false;
    }
  }

  mounted() {
    this.tick();
    // Filet : refetch régulier (capte aussi les prises en charge faites ailleurs / Centrifugo down).
    this.pollTimer = setInterval(() => this.tick(), 20000);
    // Réattire l'attention tant qu'il reste des escalades (visuel seulement, sans son).
    this.attentionTimer = setInterval(() => {
      if (this.visible) this.pulse(false);
    }, 25000);
  }

  beforeDestroy() {
    clearInterval(this.pollTimer);
    clearInterval(this.attentionTimer);
    clearTimeout(this.debounce);
    clearTimeout(this.flashTimeout);
    try {
      centrifugoManager.unsubscribe("chat:agents", this.rtHandler);
    } catch (e) {
      /* ignore */
    }
  }

  private tick() {
    if (!this.canSee) return;
    this.ensureRealtime();
    // Temps réel sain -> pas de XHR : les publications chat:agents déclenchent
    // déjà le refresh (debounced). Le polling ne reprend que si le ws tombe.
    if (this.realtimeReady && centrifugoManager.isHealthy()) return;
    this.refresh();
  }

  private async refresh() {
    try {
      const res: any = await api.get(api.chat, "conversation/escalations");
      const n = Number(res?.data?.total ?? (res?.data?.conversations || []).length ?? 0);
      const grew = n > this.count;
      this.count = isNaN(n) ? 0 : n;
      if (grew && this.count > 0) this.pulse(true); // nouvelle escalade -> pulse + son
    } catch (e) {
      /* silencieux : le polling réessaiera */
    }
  }

  private async ensureRealtime() {
    if (this.realtimeReady) return;
    try {
      const ok = await centrifugoManager.connect();
      if (!ok) return;
      // Toute activité agent -> refetch (debounced) : capte escalades ET prises en charge.
      centrifugoManager.subscribe("chat:agents", this.rtHandler);
      this.realtimeReady = true;
    } catch (e) {
      /* on reste sur le polling */
    }
  }

  private pulse(withSound: boolean) {
    if (!this.visible) return;
    this.flashing = false;
    clearTimeout(this.flashTimeout);
    this.$nextTick(() => {
      this.flashing = true;
      this.flashTimeout = setTimeout(() => {
        this.flashing = false;
      }, 1900);
    });
    if (withSound) this.playSound();
  }

  private playSound() {
    try {
      const audio = new Audio(process.env.BASE_URL + "notification.wav");
      audio.play().catch(() => {});
    } catch (e) {
      /* ignore */
    }
  }

  goToList() {
    if (this.$route.name !== "chat-escalations") {
      this.$router.push({ name: "chat-escalations" }).catch(() => {});
    }
  }
}
</script>

<style scoped>
/* Bandeau pleine largeur en tête de #kt_content_container (au-dessus du contenu de la page). */
.escalation-alert {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin-bottom: 14px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #ff1744, #c4001d);
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(196, 0, 29, 0.35);
  cursor: pointer;
  user-select: none;
  animation: esc-breathe 1.6s ease-in-out infinite;
}
.escalation-alert:hover {
  filter: brightness(1.08);
}
.esc-icon {
  font-size: 24px;
  line-height: 1;
}
.esc-body {
  display: flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.15;
}
.esc-count {
  font-size: 20px;
  font-weight: 800;
}
.esc-label {
  font-size: 13px;
  font-weight: 600;
  opacity: 0.96;
}
.esc-cta {
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.9;
  white-space: nowrap;
}
@keyframes esc-breathe {
  0%, 100% {
    box-shadow: 0 6px 18px rgba(196, 0, 29, 0.3);
  }
  50% {
    box-shadow: 0 8px 26px rgba(255, 23, 68, 0.65);
  }
}
.escalation-alert.esc-flash {
  animation: esc-flash 0.55s ease-in-out 3;
}
@keyframes esc-flash {
  0%, 100% {
    background: linear-gradient(135deg, #ff1744, #c4001d);
  }
  50% {
    background: linear-gradient(135deg, #ffea00, #ff1744);
  }
}
.esc-pop-enter-active,
.esc-pop-leave-active {
  transition: all 0.35s ease;
}
.esc-pop-enter,
.esc-pop-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
